import { Box, Chip, MenuItem, Select, SelectChangeEvent, Slider, Switch } from "@mui/material";
import React, { useEffect, useState } from "react";
import FilterBox from "./Components/FilterBox";
import { useQuery } from "@tanstack/react-query";
import { AgentFilters } from "../../../types";
import { getSkillsLists } from "../../../api/network";
import SectionHeader from "./Components/SectionHeader";
import {
  CheckedIcon,
  OverviewIcon,
  ProfessionalInfoIcon,
  SkillsIcon,
  UnCheckedIcon,
} from "../../../assets/icons/MUI/AdvancedSearch";
import { colors } from "../../../theme/palette";
import { useTranslation } from "react-i18next";

type Props = {};

type Skill = {
  id: number;
  lang: string;
  localizedId: number;
  name: string;
  role: string;
  status: string;
  subRole: string | null;
};

const AgentFilter = ({
  advancedFilters,
  setAdvancedFilters,
  isFilterCleared,
}: {
  advancedFilters: AgentFilters;
  setAdvancedFilters: React.Dispatch<React.SetStateAction<AgentFilters>>;
  isFilterCleared: boolean;
}) => {
  const { t } = useTranslation();

  const initialFilters = {
    areasCovered: advancedFilters.areasCovered || "",
    careerTransfersRange: (advancedFilters.careerTransfersRange as [number, number]) || [2, 10],
    hasFootballExperience: advancedFilters.hasFootballExperience || false,
    skills: advancedFilters.skills || ([] as number[]),
  };

  const [filters, setFilters] = useState(initialFilters);

  useEffect(() => {
    setAdvancedFilters(filters);
  }, [filters]);

  useEffect(() => {
    setFilters(initialFilters);
  }, [isFilterCleared]);

  type FilterKeys = keyof typeof filters;

  const handleSliderChange = (name: FilterKeys) => (event: Event, newValue: number | number[]) => {
    setFilters((prevState) => ({ ...prevState, [name]: newValue }));
  };

  const handleSwitchChange = (name: FilterKeys) => () => {
    setFilters((prevState) => ({ ...prevState, [name]: !prevState[name] }));
  };

  const handleSkillClick = (skill: number) => () => {
    setFilters((prevState) => {
      const newSkills = prevState.skills.includes(skill)
        ? prevState.skills.filter((s) => s !== skill)
        : [...prevState.skills, skill];
      return { ...prevState, skills: newSkills };
    });
  };

  const { data: skills } = useQuery<Skill[]>({
    queryKey: ["agentSkills"],
    queryFn: () => getSkillsLists("agent"),
    initialData: [] as Skill[],
    staleTime: 0,
  });

  return (
    <>
      <SectionHeader icon={OverviewIcon} title={t("components.networkController.advancedFilterPopUp.agentOverview")} />
      <FilterBox label={t("components.networkController.advancedFilterPopUp.areasCovered")}>
        <Select
          value={filters.areasCovered}
          onChange={(event: SelectChangeEvent) =>
            setFilters((prevState) => ({
              ...prevState,
              areasCovered: event.target.value as string,
            }))
          }
          displayEmpty
          sx={{ minWidth: "160px" }}
        >
          {skills.map((skill) => (
            <MenuItem key={skill.id} value={skill.id}>
              {skill.name}
            </MenuItem>
          ))}
        </Select>
      </FilterBox>
      <FilterBox label={t("components.networkController.advancedFilterPopUp.careerTransfers")}>
        <Slider
          value={filters.careerTransfersRange}
          onChange={handleSliderChange("careerTransfersRange")}
          valueLabelDisplay="auto"
          sx={{ maxWidth: "360px", width: "50%" }}
          min={1}
          max={100}
        />
      </FilterBox>
      <SectionHeader
        icon={ProfessionalInfoIcon}
        title={t("components.networkController.advancedFilterPopUp.professionalInfo")}
      />
      <FilterBox label={t("components.networkController.advancedFilterPopUp.hasFootballExperience")}>
        <Switch checked={filters.hasFootballExperience} onChange={handleSwitchChange("hasFootballExperience")} />
      </FilterBox>
      <SectionHeader icon={SkillsIcon} title={t("components.networkController.advancedFilterPopUp.skills")} />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px 12px",
        }}
      >
        {skills.map((skill, index) => (
          <Chip
            key={index}
            color={filters.skills.includes(skill.id) ? "primary" : "secondary"}
            label={skill.name}
            icon={
              filters.skills.includes(skill.id) ? (
                <CheckedIcon style={{ fill: "none", width: "10px", height: "10px" }} />
              ) : (
                <UnCheckedIcon style={{ fill: "none", width: "10px", height: "10px" }} />
              )
            }
            onClick={handleSkillClick(skill.id)}
            sx={{
              padding: "5px 10px",
              borderRadius: "4px",
              fontSize: "12px",
              fontWeight: "400px",
              ...(filters.skills.includes(skill.id) ? {} : { backgroundColor: colors.gray50, color: colors.black }),
            }}
          />
        ))}
      </Box>
    </>
  );
};

export default AgentFilter;
